import { Check, GripVertical, Plus, Star, X } from "lucide-react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  Droppable,
  DroppableProvided,
  DropResult,
} from "@hello-pangea/dnd";
import {
  IgButton,
  IgIconButton,
  IgPaper,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import {
  ExperienceKeyMetricFe,
  STANDARD_EVENTS_ALLOWED_KEY_METRICS,
} from "@intelligems/ig-types";
import { officialCategoryName } from "@intelligems/ig-utils";
import { useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { KeyMetricsDrawer } from "@src/features/experienceMetrics/keyMetrics/KeyMetricsDrawer";
import { useReadOnlyMode } from "@src/features/publicShareLinks/hooks/useReadOnlyMode";
import { useDialogs } from "@src/hooks/dialog";
import { initCustomEvent } from "@src/store/slices/customEvents";
import { isDrawerOpen, openDrawer } from "@src/store/slices/drawers";
import {
  getKeyMetricsRich,
  removeKeyMetric,
  reorderKeyMetrics,
  setPrimaryMetric,
} from "@src/store/slices/experienceMetrics/experienceMetrics";
import { selectedExperience } from "@src/store/slices/experiences";
import { mpClickAddMetric } from "@src/utils/mixpanel";

export const ConfigureKeyMetrics = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { isReadOnly } = useReadOnlyMode();

  const experienceKeyMetrics = useSelector(getKeyMetricsRich);
  const isKeyDrawerOpen = useSelector(isDrawerOpen("keyMetrics"));
  const experience = useSelector(selectedExperience);
  const [hover, setHover] = useImmer(-1);
  const { openDialog } = useDialogs();

  const handleClose = (keyMetric: ExperienceKeyMetricFe, index: number) => {
    if (keyMetric.experienceCustomMetric?.customEvent?.archivedAtTs) {
      openDialog("removeArchivedKeyMetric", { index });
    } else {
      dispatch(
        removeKeyMetric({
          indexToRemove: keyMetric.order,
        })
      );
    }
  };

  const onDragEnd = (result: DropResult) => {
    if (result.destination) {
      dispatch(
        reorderKeyMetrics({
          startIndex: result.source.index,
          endIndex: result.destination.index,
        })
      );
    }
  };

  const getCountOfRemainingPrimaryEligibleMetrics = () => {
    return experienceKeyMetrics.filter((keyMetric) => {
      return isKeyMetricEligibleForPrimaryMetric(keyMetric);
    }).length;
  };

  const isRemoveButtonDisabled = (keyMetric: ExperienceKeyMetricFe) => {
    return (
      getCountOfRemainingPrimaryEligibleMetrics() === 1 && keyMetric.isPrimary
    );
  };

  const isMakePrimaryButtonDisabled = (keyMetric: ExperienceKeyMetricFe) => {
    return !isKeyMetricEligibleForPrimaryMetric(keyMetric);
  };

  const isKeyMetricEligibleForPrimaryMetric = (
    keyMetric: ExperienceKeyMetricFe
  ) => {
    return (
      (!keyMetric.standardEventId && keyMetric.experienceCustomMetricId) ||
      (keyMetric.standardEventId &&
        STANDARD_EVENTS_ALLOWED_KEY_METRICS[keyMetric.standardEventId]
          .isEligibleForPrimaryMetric)
    );
  };

  const canSetPrimary = (keyMetric: ExperienceKeyMetricFe) => {
    return (
      !isReadOnly &&
      !keyMetric.isPrimary &&
      isKeyMetricEligibleForPrimaryMetric(keyMetric) &&
      experience.status === "pending"
    );
  };

  return (
    <IgPaper>
      <Grid container direction={"column"} rowSpacing={2}>
        <Grid>
          <IgTypography variant={"h2"} sx={{ fontWeight: 600 }}>
            Choose your Key Metrics and Primary Metric
          </IgTypography>
        </Grid>
        <Grid mb={1}>
          <IgTypography sx={{ color: theme.palette.text.secondary }}>
            Intelligems records 20+ metrics on every{" "}
            {officialCategoryName(experience.category, false)}. Choose up to 7
            Key Metrics to appear on the front page of your{" "}
            {officialCategoryName(experience.category, false)} results. After
            the {officialCategoryName(experience.category, false)} starts, you
            can still access other metrics and add them to your Key Metrics.
          </IgTypography>
        </Grid>
        <Grid container direction={"column"} rowSpacing={1.2}>
          <Grid>
            <IgPaper
              sx={{
                borderRadius: "5px",
                padding: "15px 22px",
                boxShadow: "none",
                backgroundColor: theme.igPrimitives.gray[200],
              }}
              elevation={0}
            >
              <Grid
                container
                direction={"row"}
                alignItems={"center"}
                wrap={"nowrap"}
                columnSpacing={1.2}
              >
                <Grid>
                  <Check color={theme.igIconColors.success} />
                </Grid>
                <Grid container direction={"column"}>
                  <Grid>
                    <IgTypography variant={"h3"} sx={{ fontWeight: 600 }}>
                      Visitors, Orders & Revenue
                    </IgTypography>
                  </Grid>
                  <Grid>
                    <IgTypography
                      sx={{
                        color: theme.palette.text.secondary,
                      }}
                    >
                      These metrics are shown by default in every{" "}
                      {officialCategoryName(experience.category, false)}
                    </IgTypography>
                  </Grid>
                </Grid>
              </Grid>
            </IgPaper>
          </Grid>
          <Grid>
            {/* @ts-ignore */}
            <DragDropContext onDragEnd={onDragEnd}>
              {/* @ts-ignore */}
              <Droppable
                droppableId={"key-metric-zone"}
                direction={"vertical"}
                isDropDisabled={false}
              >
                {(provided: DroppableProvided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    {experienceKeyMetrics.map(
                      (keyMetric: ExperienceKeyMetricFe, index: number) => {
                        return (
                          // @ts-ignore
                          <Draggable
                            key={`key-${keyMetric.standardEventId || keyMetric.experienceCustomMetricId}`}
                            draggableId={`${keyMetric.standardEventId || keyMetric.experienceCustomMetricId}`}
                            index={index}
                            isDragDisabled={
                              isReadOnly || experienceKeyMetrics.length === 1
                            }
                          >
                            {(
                              dragProvided: DraggableProvided,
                              dragSnapshot: DraggableStateSnapshot
                            ) => {
                              let transform =
                                dragProvided.draggableProps.style!.transform;

                              if (dragSnapshot.isDragging && transform) {
                                transform = transform.replace(/\(.+,/, "(0,");
                              }

                              const style = {
                                ...dragProvided.draggableProps.style,
                                transform,
                              };
                              return (
                                <Grid
                                  key={`key-inner-${keyMetric.standardEventId || keyMetric.experienceCustomMetricId}`}
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  style={style}
                                >
                                  <IgPaper
                                    onMouseEnter={() => {
                                      setHover(index);
                                    }}
                                    onMouseLeave={() => {
                                      setHover(-1);
                                    }}
                                    onClick={() => {
                                      if (!canSetPrimary(keyMetric)) return;
                                      dispatch(
                                        setPrimaryMetric({
                                          currentIndex: index,
                                        })
                                      );
                                    }}
                                    sx={{
                                      borderRadius: "5px",
                                      padding: "15px 22px",
                                      boxShadow: "none",
                                      "&:hover": {
                                        cursor: canSetPrimary(keyMetric)
                                          ? "pointer"
                                          : "default",
                                      },
                                    }}
                                    elevation={0}
                                  >
                                    <Grid
                                      container
                                      direction={"row"}
                                      alignItems={"center"}
                                      wrap={"nowrap"}
                                      justifyContent={"space-between"}
                                      sx={{ height: "47px" }}
                                    >
                                      <Grid
                                        alignItems={"center"}
                                        wrap={"nowrap"}
                                        container
                                        columnSpacing={1.2}
                                        direction={"row"}
                                        size={10.5}
                                      >
                                        <Grid
                                          {...dragProvided.dragHandleProps}
                                          onClick={(e: React.MouseEvent) =>
                                            e.stopPropagation()
                                          }
                                          sx={{
                                            visibility:
                                              isReadOnly || hover !== index
                                                ? "hidden"
                                                : "visible",
                                            height: "32px",
                                            width: "32px",
                                          }}
                                        >
                                          <IgTooltip title={"Drag to reorder"}>
                                            <IgIconButton>
                                              <GripVertical size={16} />
                                            </IgIconButton>
                                          </IgTooltip>
                                        </Grid>
                                        <Grid
                                          container
                                          direction={"column"}
                                          wrap={"nowrap"}
                                        >
                                          <Grid container direction={"row"}>
                                            <Grid>
                                              <IgTypography
                                                onClick={
                                                  isReadOnly
                                                    ? undefined
                                                    : (e) => {
                                                        e.stopPropagation();
                                                        if (
                                                          keyMetric
                                                            ?.experienceCustomMetric
                                                            ?.customEvent
                                                        ) {
                                                          dispatch(
                                                            initCustomEvent({
                                                              customEvent:
                                                                keyMetric
                                                                  .experienceCustomMetric
                                                                  .customEvent,
                                                            })
                                                          );
                                                        }
                                                        dispatch(
                                                          openDrawer({
                                                            type: "keyMetrics",
                                                          })
                                                        );
                                                      }
                                                }
                                                variant={"h3"}
                                                sx={{
                                                  fontWeight: 600,
                                                  cursor: isReadOnly
                                                    ? "default"
                                                    : "pointer",
                                                }}
                                              >
                                                {keyMetric?.experienceCustomMetricId
                                                  ? keyMetric
                                                      .experienceCustomMetric
                                                      ?.customEvent?.name
                                                  : keyMetric.standardEventId &&
                                                      STANDARD_EVENTS_ALLOWED_KEY_METRICS[
                                                        keyMetric
                                                          .standardEventId
                                                      ]?.name
                                                    ? STANDARD_EVENTS_ALLOWED_KEY_METRICS[
                                                        keyMetric
                                                          .standardEventId
                                                      ]?.name
                                                    : "Unknown Metric"}
                                              </IgTypography>
                                            </Grid>
                                          </Grid>
                                          <Grid>
                                            <IgTypography
                                              color={
                                                theme.palette.text.secondary
                                              }
                                            >
                                              {keyMetric?.experienceCustomMetricId
                                                ? keyMetric
                                                    .experienceCustomMetric
                                                    ?.customEvent?.description
                                                : keyMetric.standardEventId
                                                  ? STANDARD_EVENTS_ALLOWED_KEY_METRICS[
                                                      keyMetric.standardEventId
                                                    ].description
                                                  : ""}
                                            </IgTypography>
                                          </Grid>
                                        </Grid>
                                      </Grid>
                                      {(isReadOnly && keyMetric.isPrimary) ||
                                      (!isReadOnly &&
                                        (hover === index ||
                                          keyMetric.isPrimary)) ? (
                                        <Grid
                                          container
                                          direction={"row"}
                                          alignItems={"center"}
                                          wrap={"nowrap"}
                                          justifyContent={"end"}
                                          sx={{ width: "157px" }}
                                          size={1.7}
                                          columnSpacing={1}
                                        >
                                          <Grid>
                                            {/*Sets Primary Metrics*/}
                                            {isReadOnly ? (
                                              // Read-only: just show "Primary" label with star
                                              keyMetric.isPrimary && (
                                                <IgButton
                                                  disableRipple
                                                  sx={{ cursor: "default" }}
                                                  startIcon={
                                                    <Star
                                                      size={18}
                                                      color={
                                                        theme.igPrimitives
                                                          .yellow[500]
                                                      }
                                                    />
                                                  }
                                                  size={"small"}
                                                >
                                                  Primary
                                                </IgButton>
                                              )
                                            ) : (
                                              <IgTooltip
                                                title={
                                                  isMakePrimaryButtonDisabled(
                                                    keyMetric
                                                  )
                                                    ? "This Key Metric is not eligible to be set as primary"
                                                    : ""
                                                }
                                              >
                                                <IgButton
                                                  disabled={isMakePrimaryButtonDisabled(
                                                    keyMetric
                                                  )}
                                                  disableRipple={
                                                    keyMetric.isPrimary
                                                  }
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (!keyMetric.isPrimary) {
                                                      dispatch(
                                                        setPrimaryMetric({
                                                          currentIndex: index,
                                                        })
                                                      );
                                                    }
                                                  }}
                                                  sx={{
                                                    display:
                                                      experience.status !==
                                                        "pending" &&
                                                      !keyMetric.isPrimary
                                                        ? "none"
                                                        : "flex",
                                                    cursor: keyMetric.isPrimary
                                                      ? "default"
                                                      : "pointer",
                                                  }}
                                                  startIcon={
                                                    keyMetric.isPrimary ? (
                                                      <Star
                                                        size={18}
                                                        color={
                                                          theme.igPrimitives
                                                            .yellow[500]
                                                        }
                                                      />
                                                    ) : null
                                                  }
                                                  size={"small"}
                                                >
                                                  {keyMetric.isPrimary
                                                    ? "Primary"
                                                    : "Make Primary"}
                                                </IgButton>
                                              </IgTooltip>
                                            )}
                                          </Grid>
                                          <div></div>
                                          {!isReadOnly &&
                                          experience.status !== "pending" &&
                                          keyMetric.isPrimary ? null : !isReadOnly &&
                                            experienceKeyMetrics.length > 1 &&
                                            hover === index ? (
                                            <Grid>
                                              <IgTooltip
                                                title={
                                                  isRemoveButtonDisabled(
                                                    keyMetric
                                                  )
                                                    ? "The last eligible Primary Key Metric can not be removed"
                                                    : ""
                                                }
                                              >
                                                <IgIconButton
                                                  disabled={isRemoveButtonDisabled(
                                                    keyMetric
                                                  )}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleClose(
                                                      keyMetric,
                                                      index
                                                    );
                                                  }}
                                                >
                                                  <X size={16} />
                                                </IgIconButton>
                                              </IgTooltip>
                                            </Grid>
                                          ) : null}
                                        </Grid>
                                      ) : (
                                        <Grid
                                          sx={{ width: "147px" }}
                                          size={1.5}
                                        />
                                      )}
                                    </Grid>
                                  </IgPaper>
                                </Grid>
                              );
                            }}
                          </Draggable>
                        );
                      }
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Grid>
        </Grid>
        {!isReadOnly && (
          <Grid mt={3}>
            <IgTooltip
              title={
                experienceKeyMetrics.length === 7
                  ? `A max of 7 Key Metrics can be added to a ${officialCategoryName(experience.category, false)}`
                  : ""
              }
            >
              <IgButton
                disabled={experienceKeyMetrics.length === 7}
                startIcon={<Plus size={18} color={theme.igIconColors.dark} />}
                onClick={() => {
                  mpClickAddMetric(experience?.id, "Key Metric");
                  dispatch(openDrawer({ type: "keyMetrics" }));
                }}
              >
                Add Key Metric
              </IgButton>
            </IgTooltip>
          </Grid>
        )}
      </Grid>
      {!isReadOnly && isKeyDrawerOpen && <KeyMetricsDrawer />}
    </IgPaper>
  );
};
